/* eslint-disable react/no-array-index-key */
import React, { useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
} from "@material-ui/core"
import { Delete, Warning, CheckCircle, Devices, Face } from "@material-ui/icons"
import { makeStyles } from "@material-ui/core/styles"
import { useSnackbar } from "notistack"
import axiosInstance from "src/utils/axios"
import { Alert } from "@material-ui/lab"

const useStyles = makeStyles(theme => ({
  warningBox: {
    backgroundColor: "#fef3c7",
    border: "1px solid #f59e0b",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}))

const DeleteRostro = ({
  selectedRostro,
  idConjunto,
  onDelete = () => {},
  onClose = () => {},
  disabled = false,
  setAllUniqueFaces,
}) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [deleteSuccess, setDeleteSuccess] = useState(false)
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([])

  const handleOpen = () => {
    setOpen(true)
    // Inicializar con todos los dispositivos seleccionados por defecto
    setSelectedDeviceIds(selectedRostro?.devices?.map(d => d.deviceId) || [])
  }

  const handleClose = () => {
    setOpen(false)
    setDeleteSuccess(false)
    setSelectedDeviceIds([])
    onClose()
  }

  const handleDeviceSelectionChange = event => {
    setSelectedDeviceIds(event.target.value)
  }

  const handleDelete = async () => {
    if (selectedDeviceIds.length === 0) {
      enqueueSnackbar("Debe seleccionar al menos un dispositivo", {
        variant: "warning",
      })
      return
    }

    setLoading(true)

    try {
      const response = await axiosInstance.delete(
        `/api/biometrics/conjuntos/${idConjunto}/rostros/${selectedRostro.identificacion}/delete/`,
        {
          data: {
            dispositivos_biometricos: JSON.stringify(selectedDeviceIds),
          },
        }
      )

      if (response.status === 200 || response.status === 207) {
        const { data } = response.data
        setDeleteSuccess(true)

        let message = `Rostro eliminado en ${data.successful_count} dispositivo(s)`

        if (data.failed_count > 0) {
          message += ` (${data.failed_count} fallaron)`
        }

        if (data.skipped_count > 0) {
          message += ` (${data.skipped_count} omitidos)`
        }

        enqueueSnackbar(message, {
          variant: data.failed_count > 0 ? "warning" : "success",
        })

        if (data.successful_count > 0) {
          // Usar los dispositivos que se intentaron eliminar como base
          // y filtrar solo los que fueron exitosos según la respuesta del servidor
          const successfulDeviceIds = data.successful_devices.map(
            device => device.biometric_id
          )

          setAllUniqueFaces(prevFaces => {
            const updatedFaces = prevFaces
              .map(face => {
                if (face.identificacion === selectedRostro.identificacion) {
                  // Filtrar dispositivos: mantener solo los que NO fueron eliminados exitosamente
                  const updatedDevices =
                    face.devices?.filter(
                      device =>
                        // Usar deviceId para la comparación (que coincide con selectedDeviceIds)
                        !successfulDeviceIds.includes(device.deviceId)
                    ) || []

                  // Si no quedan dispositivos, eliminar el rostro completamente
                  if (updatedDevices.length === 0) {
                    return null
                  }

                  // Retornar rostro con dispositivos actualizados
                  return {
                    ...face,
                    devices: updatedDevices,
                    deviceCount: updatedDevices.length,
                  }
                }
                return face
              })
              .filter(Boolean) // Filtrar rostros que son null

            return updatedFaces
          })

          if (onDelete) {
            onDelete(selectedRostro.id)
          }
        }

        setTimeout(() => handleClose(), 2000)
      } else {
        throw new Error(response.data.error || "Error eliminando rostro")
      }
    } catch (error) {
      if (error.response) {
        const errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          `Error del servidor: ${error.response.status}`
        enqueueSnackbar(errorMessage, { variant: "error" })
      } else if (error.request) {
        enqueueSnackbar("Error de conexión. Verifique su internet.", {
          variant: "error",
        })
      } else {
        enqueueSnackbar(`Error inesperado: ${error.message}`, {
          variant: "error",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Tooltip title="Eliminar Rostro">
        <IconButton
          color="secondary"
          onClick={handleOpen}
          size="small"
          disabled={!selectedRostro || disabled}
          style={{ color: "#ef4444" }}
        >
          <Delete />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={!loading ? handleClose : undefined}
        maxWidth="md"
        fullWidth
        disableBackdropClick={loading}
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {deleteSuccess ? (
              <>
                <CheckCircle style={{ color: "#22c55e" }} />
                Eliminación Completada
              </>
            ) : (
              <>
                <Warning style={{ color: "#ef4444" }} />
                Confirmar Eliminación
              </>
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {!deleteSuccess ? (
            <DialogContentText component="div">
              <Grid container spacing={3}>
                {/* Información del rostro */}
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={2}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Rostro a eliminar
                    </Typography>

                    <Box
                      style={{
                        width: 180,
                        height: 180,
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: "3px solid #e2e8f0",
                        backgroundColor: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {selectedRostro?.imagen_url ||
                      selectedRostro?.imagenUrl ? (
                        <img
                          src={
                            selectedRostro?.imagen_url ||
                            selectedRostro?.imagenUrl
                          }
                          alt={selectedRostro?.nombre}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Face style={{ fontSize: 60, color: "#cbd5e0" }} />
                      )}
                    </Box>

                    <Typography
                      variant="h6"
                      align="center"
                      style={{ fontWeight: "600" }}
                    >
                      {selectedRostro?.nombre}
                    </Typography>

                    <Box
                      display="flex"
                      gap={1}
                      flexWrap="wrap"
                      justifyContent="center"
                    >
                      <Chip
                        label={`ID: ${selectedRostro?.identificacion}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={selectedRostro?.tipo || "N/A"}
                        size="small"
                        style={{ backgroundColor: "#3b82f6", color: "white" }}
                      />
                      <Chip
                        label={selectedRostro?.estado || "N/A"}
                        size="small"
                        style={{
                          backgroundColor:
                            selectedRostro?.estado === "activo"
                              ? "#22c55e"
                              : "#6b7280",
                          color: "white",
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="textSecondary"
                      align="center"
                    >
                      Destino: {selectedRostro?.destino || "No especificado"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Selección de dispositivos */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        required
                        error={selectedDeviceIds.length === 0}
                      >
                        <InputLabel>Dispositivos Biométricos</InputLabel>
                        <Select
                          multiple
                          value={selectedDeviceIds}
                          onChange={handleDeviceSelectionChange}
                          renderValue={selected => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map(deviceId => {
                                const device = selectedRostro?.devices?.find(
                                  d => d.deviceId === deviceId
                                )
                                return (
                                  <Chip
                                    key={deviceId}
                                    label={
                                      device
                                        ? `${device.deviceName} (${device.deviceUbicacion})`
                                        : deviceId
                                    }
                                    size="small"
                                    icon={<Devices style={{ fontSize: 16 }} />}
                                  />
                                )
                              })}
                            </Box>
                          )}
                        >
                          {selectedRostro?.devices?.length === 0 ? (
                            <MenuItem disabled>
                              <Typography variant="body2">
                                No hay dispositivos disponibles
                              </Typography>
                            </MenuItem>
                          ) : (
                            selectedRostro?.devices?.map(device => (
                              <MenuItem
                                key={device.deviceId}
                                value={device.deviceId}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  width="100%"
                                >
                                  <Devices
                                    style={{
                                      marginRight: 8,
                                      fontSize: 18,
                                      color: "#1e293b",
                                    }}
                                  />
                                  <Box flex={1}>
                                    <Typography
                                      variant="body2"
                                      style={{ fontWeight: "600" }}
                                    >
                                      {device.deviceName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {device.deviceUbicacion} -{" "}
                                      {device.deviceMarca}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        <FormHelperText>
                          {selectedDeviceIds.length === 0
                            ? "Debe seleccionar al menos un dispositivo"
                            : `${selectedDeviceIds.length} dispositivo(s) seleccionado(s) para eliminación`}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Box className={classes.warningBox}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Warning style={{ color: "#f59e0b", marginRight: 8 }} />
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: "600", color: "#92400e" }}
                  >
                    Advertencia
                  </Typography>
                </Box>
                <Typography variant="body2" style={{ color: "#92400e" }}>
                  Esta acción eliminará permanentemente el rostro de{" "}
                  {selectedDeviceIds.length} dispositivo(s) seleccionado(s).
                  Esta operación no se puede deshacer.
                </Typography>
              </Box>
              <Alert severity="info" style={{ marginTop: 8 }}>
                Los cambios se sincronizarán en los dispositivos biométricos en
                aproximadamente 30 minutos.
              </Alert>
              <Box mt={3}>
                <Typography
                  variant="body1"
                  align="center"
                  style={{ fontWeight: "500" }}
                >
                  ¿Está seguro que desea eliminar este rostro?
                </Typography>
              </Box>
            </DialogContentText>
          ) : (
            <DialogContentText component="div">
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
              >
                <CheckCircle style={{ fontSize: 48, color: "#22c55e" }} />

                <Typography
                  variant="h6"
                  align="center"
                  style={{ fontWeight: "600" }}
                >
                  Rostro eliminado exitosamente
                </Typography>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                >
                  El rostro ha sido eliminado de los dispositivos seleccionados.
                </Typography>
              </Box>
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions>
          {!deleteSuccess ? (
            <>
              <Button
                onClick={handleClose}
                disabled={loading}
                style={{ textTransform: "none" }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                disabled={loading || selectedDeviceIds.length === 0}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} style={{ color: "white" }} />
                  ) : (
                    <Delete style={{ color: "white" }} />
                  )
                }
                style={{
                  backgroundColor:
                    selectedDeviceIds.length === 0 ? "#9ca3af" : "#ef4444",
                  color: "white",
                  textTransform: "none",
                }}
              >
                {loading
                  ? "Eliminando..."
                  : `Eliminar de ${selectedDeviceIds.length} dispositivo(s)`}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleClose}
              variant="contained"
              style={{
                backgroundColor: "#3b82f6",
                color: "white",
                textTransform: "none",
              }}
            >
              Cerrar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DeleteRostro
